// An endless row that can be dragged or swiped; on release it glides to the nearest
// card. Desktop also drifts on its own — on phones the row only moves on a swipe.
const desktop = window.matchMedia('(min-width: 641px)');
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const GLIDE_MS = 520;
const GLIDE = `transform ${GLIDE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;
const SPEED = 28; // px per second

function bind(root: HTMLElement): void {
	const track = root.querySelector<HTMLElement>('[data-armada-snap-track]');

	if (!track || track.children.length < 2) {
		return;
	}

	const row = track;
	const originals = Array.from(row.children) as HTMLElement[];
	const count = originals.length;
	const dots = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-armada-snap-dot]'));
	let active = false;
	let index = 0;
	let x = 0;
	let pitch = 0;
	let start = 0;
	let gliding = false;
	let glideTimer = 0;
	let pointer = -1;

	// Copies before and after the real cards, so there is always a card to move to.
	const copy = (item: HTMLElement): HTMLElement => {
		const clone = item.cloneNode(true) as HTMLElement;
		clone.setAttribute('data-clone', '');
		clone.setAttribute('aria-hidden', 'true');
		clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));

		return clone;
	};

	const place = (value: number, animate: boolean): void => {
		x = value;
		row.style.transition = animate && !reduced.matches ? GLIDE : 'none';
		row.style.transform = `translate3d(${-x}px, 0, 0)`;
	};

	// Offset that shows DOM child `i` as the left card of the centred pair.
	const offsetFor = (i: number): number => i * pitch - start;

	const measure = (): void => {
		const first = row.children[0] as HTMLElement;
		const second = row.children[1] as HTMLElement;
		pitch = second.offsetLeft - first.offsetLeft;
		// Desktop centres a pair of cards; phones leave the first one at the track's inset.
		start = desktop.matches ? (root.clientWidth - (2 * pitch - (pitch - first.offsetWidth))) / 2 : 0;
	};

	// One dot per card, lit for whichever real card the row currently starts on.
	const markDots = (): void => {
		const current = ((index % count) + count) % count;

		dots.forEach((dot, i) => {
			dot.classList.toggle('is-active', i === current);
			dot.setAttribute('aria-current', i === current ? 'true' : 'false');
		});
	};

	const goTo = (i: number, animate = true): void => {
		index = i;
		place(offsetFor(index), animate);
		markDots();

		gliding = animate && !reduced.matches;
		window.clearTimeout(glideTimer);

		// transitionend can be skipped (e.g. hidden tab), so time the glide too.
		if (gliding) {
			glideTimer = window.setTimeout(() => {
				gliding = false;
				settle();
			}, GLIDE_MS + 60);
		}
	};

	// Back from the copies onto the matching real card, without motion.
	function settle(): void {
		if (index < count || index >= 2 * count) {
			goTo(((index % count) + count) % count + count, false);
		}
	}

	// Autoplay: speed eases towards 1 while free and towards 0 on hover, focus,
	// drag or when the row is out of view, so it never starts or stops abruptly.
	let hovered = false;
	let visible = false;
	let speed = 0;
	let frame = 0;
	let last = 0;

	const tick = (now: number): void => {
		frame = window.requestAnimationFrame(tick);

		const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
		last = now;

		const free = !hovered && visible && pointer < 0 && !gliding;
		speed += ((free ? 1 : 0) - speed) * Math.min(1, dt * 2.5);

		if (pointer >= 0 || gliding || speed < 0.002) {
			return;
		}

		let next = x + SPEED * speed * dt;

		// Past the real cards: step back one set, which looks identical.
		if (next + start >= 2 * count * pitch) {
			next -= count * pitch;
		}

		place(next, false);
		index = Math.round((x + start) / pitch);
		markDots();
	};

	const play = (): void => {
		if (!frame && !reduced.matches) {
			last = 0;
			frame = window.requestAnimationFrame(tick);
		}
	};

	const stop = (): void => {
		window.cancelAnimationFrame(frame);
		frame = 0;
		speed = 0;
	};

	root.addEventListener('mouseenter', () => {
		hovered = true;
	});

	root.addEventListener('mouseleave', () => {
		hovered = false;
	});

	root.addEventListener('focusin', () => {
		hovered = true;
	});

	root.addEventListener('focusout', (event) => {
		hovered = root.contains(event.relatedTarget as Node | null);
	});

	new IntersectionObserver(([entry]) => {
		visible = entry.isIntersecting;
	}).observe(root);

	const enable = (): void => {
		if (active) {
			return;
		}

		active = true;
		row.prepend(...originals.map(copy));
		row.append(...originals.map(copy));
		root.classList.add('is-loop');
		measure();
		goTo(count, false);
	};

	row.addEventListener('transitionend', (event) => {
		if (event.target === row && gliding) {
			gliding = false;
			window.clearTimeout(glideTimer);
			settle();
		}
	});

	// Dragging.
	let startX = 0;
	let startOffset = 0;
	let lastX = 0;
	let lastTime = 0;
	let velocity = 0;
	let moved = false;

	row.addEventListener('pointerdown', (event) => {
		if (!active || event.button !== 0) {
			return;
		}

		// Grabbed mid-glide: carry on from where the row is on screen, not its target.
		const current = new DOMMatrixReadOnly(getComputedStyle(row).transform);
		const span = count * pitch;
		let from = -current.m41;

		if (from + start < count * pitch - pitch / 2) {
			from += span;
		} else if (from + start >= 2 * count * pitch - pitch / 2) {
			from -= span;
		}

		place(from, false);
		index = Math.round((from + start) / pitch);
		gliding = false;
		window.clearTimeout(glideTimer);
		pointer = event.pointerId;
		startX = lastX = event.clientX;
		lastTime = event.timeStamp;
		velocity = 0;
		startOffset = x;
		moved = false;
		row.classList.add('is-dragging');
	});

	row.addEventListener('pointermove', (event) => {
		if (event.pointerId !== pointer) {
			return;
		}

		const dx = event.clientX - startX;

		if (!moved && Math.abs(dx) > 4) {
			moved = true;
			row.setPointerCapture(pointer);
		}

		if (event.timeStamp > lastTime) {
			velocity = (event.clientX - lastX) / (event.timeStamp - lastTime);
		}

		lastX = event.clientX;
		lastTime = event.timeStamp;
		place(startOffset - dx, false);
	});

	const release = (event: PointerEvent): void => {
		if (event.pointerId !== pointer) {
			return;
		}

		pointer = -1;
		row.classList.remove('is-dragging');

		const from = Math.round((startOffset + start) / pitch);
		let target = Math.round((x + start) / pitch);

		// A quick flick moves at least one card even if it barely travelled.
		if (target === from && Math.abs(velocity) > 0.3) {
			target += velocity < 0 ? 1 : -1;
		}

		goTo(Math.max(0, Math.min(3 * count - 1, target)));
	};

	row.addEventListener('pointerup', release);
	row.addEventListener('pointercancel', release);

	// A drag that ends over a link should not open it.
	row.addEventListener(
		'click',
		(event) => {
			if (moved) {
				event.preventDefault();
				event.stopPropagation();
				moved = false;
			}
		},
		true
	);

	row.addEventListener('dragstart', (event) => event.preventDefault());

	const sync = (): void => {
		enable();
		measure();
		goTo(index, false);

		// The row drifts by itself on desktop only; on phones it waits for a swipe.
		if (desktop.matches) {
			play();
		} else {
			stop();
		}
	};

	if (dots.length === count) {
		dots.forEach((dot, i) => {
			dot.addEventListener('click', () => {
				if (active) {
					goTo(count + i);
				}
			});
		});
	}

	window.addEventListener('resize', () => {
		if (active) {
			measure();
			goTo(index, false);
		}
	});

	sync();
	desktop.addEventListener('change', sync);
}

export default function initLoop(): void {
	document.querySelectorAll<HTMLElement>('[data-armada-loop]').forEach(bind);
}
